import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { DataGrid } from "@mui/x-data-grid";
import { Button, Chip } from "@mui/material";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import "../../../components/common/css/datatable.scss";
import useFocusTrap from "../../../hooks/useFocusTrap";
import { useRole } from "../../../hooks/useRole";
import {
  formatMoney,
  listRequestBody,
  markPaidOutcome,
  receiptLabel,
  rowCredit,
  receiptDifference,
  reversalReasonError,
  creditDiffers,
  differingCreditWarning,
  describeRefusal,
  reversalConfirmation,
  reversalOutcome,
  proofFailure,
  proofStatus,
  markPaidProofNote,
  MARK_PAID_PROOF_HINT,
  canRequestNewProof,
  proofReasonError,
  newProofRequestedOutcome,
  proofDownloadName,
  PROOF_REASON_MIN,
  PROOF_REASON_MAX,
} from "./wireReceiptUi";

/**
 * Wire reconciliation — non-Canadian investors, and everyone paying a USD
 * campaign, pay by international wire quoting their per-investment payment
 * reference (RFM-…). When the credit shows up at the bank, match it here and
 * mark the order PAID with the bank's reference. Investors can pre-declare
 * "wire sent" (with an amount, a currency and an optional receipt) from the
 * payment step; declared rows float to the top.
 *
 * ── CURRENCY (2026-08-02) ──────────────────────────────────────────────────
 * This screen is the one moment a human matches a bank credit to an order, and
 * it used to show neither the order's currency nor the campaign's — just a bare
 * `amount` with no formatter. Under the no-conversion policy a campaign is
 * listed in CAD or USD and wire carries all USD volume plus every
 * non-Canadian's CAD payment, so a reconciler was looking at a number with no
 * reliable unit and settling on it.
 *
 * Three separate figures are now shown, because the whole point of holding them
 * is that they can disagree:
 *   ORDERED   what the order is denominated in (derived server-side from the
 *             campaign since the order-currency change).
 *   DECLARED  what the INVESTOR says they sent.
 *   RECEIVED  what the BANK actually credited, keyed here.
 *
 * markWirePaid REQUIRES the received amount and currency and refuses a currency
 * that disagrees with the campaign unless an explicit reason is recorded, so
 * this form cannot be simplified back into a single bank-reference prompt
 * without breaking settlement outright.
 *
 * ── R8 / D9 (2026-10-07): NO TOLERANCE, NO PARTIAL PAYMENTS ───────────────
 * Every credit recorded here is kept as a wire receipt. Only an exact credit
 * in the payable currency settles the order (200). A short or excess credit,
 * or one in another currency recorded with a reason, is an unresolved
 * mismatch (202): the order stays unpaid, the receipt is listed under "Open
 * mismatches", and a second credit is refused until the AD reverses the
 * receipt (with a reason of at least 20 characters) and records the corrected
 * one. Reversing a MATCHED receipt reverses its settlement. These actions are
 * AD-only and audited.
 *
 * THE SERVER DECIDES (M4, 2026-10-07). This page gates nothing on its own: a
 * zero or negative credit is posted and the server's 400 is shown, the AD-only
 * actions are offered and the server's 403 is shown, and every refusal is
 * worded from the API's `code` (wireReceiptUi.describeRefusal). A mismatch is
 * reported with ordered vs credited amount and currency; a reversal takes a
 * reason, then a separate confirmation of what moves, before it is posted.
 *
 * ── WIRE PROOF OF PAYMENT (2026-10-19) ─────────────────────────────────────
 * Investors upload a PDF proof from their dashboard. The Proof column states
 * where each order is (awaiting, overdue, new proof requested, received and
 * when the order may expire unless verified, verified), in America/Toronto
 * time; the "Proof received, not verified" filter asks for proofReceivedOnly.
 * The AD verifies a payment with Mark paid (bank reference + exact credit): a
 * proof NEVER gates it, the Mark paid dialog only warns. The one exception to
 * "this page gates nothing" is "Request new proof" (decision D5): it is shown
 * only to the AD and only on a received proof, because its reason goes to the
 * investor. The server still decides (requireRole('AD'); a proof replaced
 * after the AD looked is refused PROOF_REPLACED).
 */
const API = process.env.REACT_APP_API;

function authHeaders() {
  return { "x-access-token": localStorage.getItem("accessToken") };
}

/**
 * "4,000.00 USD" — always an explicit ISO code, never a symbol (see
 * wireReceiptUi.formatMoney, shared with the receipt outcome dialogs).
 */
const money = formatMoney;

function escapeHtml(value) {
  return String(value === null || value === undefined ? "" : value).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );
}

/** The proof note in the Mark paid dialog: a warning to read, never a gate. */
function proofNoteHtml(row) {
  const note = markPaidProofNote(row);
  if (!note) return "";
  const colors = note.tone === "warning"
    ? "background:var(--warn-100, #FBEFD3);color:var(--warn-700, #8A5A00)"
    : "background:var(--info-100, #D9EAF6);color:var(--info-700, #0E5283)";
  const hint = note.tone === "warning" ? ` ${escapeHtml(MARK_PAID_PROOF_HINT)}` : "";
  return `<p data-testid="mark-paid-proof-note" data-tone="${note.tone}"
             style="margin:0 0 10px;padding:8px 10px;border-radius:4px;${colors}">
            Proof of payment: ${escapeHtml(note.text)}${hint}
          </p>`;
}

const backdropStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
  zIndex: 1300, display: "grid", placeItems: "center", padding: 16,
};
const dialogStyle = {
  background: "var(--bg-card, #fff)", border: "1px solid var(--bd, #ddd)",
  borderRadius: "var(--r-md, 4px)", padding: 20, maxWidth: 560, width: "100%",
  maxHeight: "90vh", overflow: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
};

/**
 * Ask the investor for a new proof of payment (AD only, D5). Focus-trapped and
 * labelled; Escape and Cancel close it unless the request is in flight. The
 * reason is trimmed, 20 to 500 characters, and shown to the investor.
 */
function RequestNewProofDialog({ row, submitting, onCancel, onSubmit }) {
  const [reason, setReason] = useState("");
  const dialogRef = useFocusTrap({
    active: true,
    onClose: () => {
      if (!submitting) onCancel();
    },
  });
  const length = reason.trim().length;
  const error = proofReasonError(reason);
  const status = proofStatus(row.wireProof);

  const submit = (ev) => {
    ev.preventDefault();
    if (error || submitting) return;
    onSubmit(reason.trim());
  };

  return (
    <div style={backdropStyle}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-new-proof-title"
        aria-describedby="request-new-proof-desc"
        data-testid="request-new-proof-dialog"
        style={dialogStyle}
      >
        <form onSubmit={submit} noValidate>
          <h2 id="request-new-proof-title" style={{ fontSize: 20, fontWeight: 600, margin: "0 0 8px" }}>
            Request a new proof for order #{row.investmentId}
          </h2>
          <p id="request-new-proof-desc" style={{ margin: "0 0 12px", fontSize: 14 }}>
            The investor will see this reason and will be asked to upload a new PDF.
            Daily reminders resume.
          </p>
          {status && (
            <p style={{ margin: "0 0 12px", fontSize: 13 }}>
              {row.investor ? `${row.investor}. ` : ""}
              {status.label}.
            </p>
          )}
          <label htmlFor="request-new-proof-reason" style={{ display: "block", fontSize: 14, fontWeight: 600 }}>
            Reason for the investor
          </label>
          <textarea
            id="request-new-proof-reason"
            rows={5}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            aria-invalid={length > 0 && !!error}
            aria-describedby="request-new-proof-count"
            style={{ width: "100%", marginTop: 4, padding: 8, fontSize: 14, boxSizing: "border-box" }}
          />
          <p
            id="request-new-proof-count"
            data-testid="request-new-proof-count"
            style={{ margin: "4px 0 0", fontSize: 12, color: length > 0 && error ? "#b71c1c" : "inherit" }}
          >
            {length} / {PROOF_REASON_MAX} characters (at least {PROOF_REASON_MIN}).
            {length > 0 && error ? ` ${error}` : ""}
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button type="button" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="warning"
              disabled={!!error || submitting}
              aria-busy={submitting}
              data-testid="request-new-proof-submit"
            >
              {submitting ? "Requesting…" : "Request new proof"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

const WireReconciliation = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("UNPAID");
  // Wire proof of payment: the row whose new proof is being asked for, the
  // request in flight, and the proof being downloaded.
  const [proofRequest, setProofRequest] = useState(null);
  const [pendingProofRefusal, setPendingProofRefusal] = useState(null);
  const [proofSubmitting, setProofSubmitting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  // D5: asking the investor for a new proof is the AD's (hasRole follows the
  // server's requireRole, Super Admin wildcard included).
  const { hasRole } = useRole();
  const canAskForProof = hasRole("AD");

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}admin/wire/list`,
        listRequestBody(statusFilter),
        { headers: authHeaders() }
      );
      setRows(res?.data?.investments || []);
    } catch (error) {
      Swal.fire({
        title: error?.response?.data?.message || "Failed to load wire queue",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  /**
   * Post one attempt. A mismatched currency comes back 409
   * RECEIVED_CURRENCY_MISMATCH; the caller re-posts with a reason.
   */
  const postMarkPaid = (payload) =>
    axios.post(`${API}admin/wire/mark-paid`, payload, { headers: authHeaders() });

  /**
   * R8: a 202 is a recorded mismatch, never a payment: say so, with what was
   * ordered against what the bank credited. A 200 is the receipt that settled.
   */
  const reportOutcome = async (response, row) => {
    const outcome = markPaidOutcome(response, row);
    if (outcome.settled) {
      await Swal.fire({ icon: outcome.icon, title: outcome.title, text: outcome.text, confirmButtonText: "OK" });
      return;
    }
    const s = outcome.summary;
    await Swal.fire({
      icon: outcome.icon,
      title: outcome.title,
      html: `
        <div style="text-align:left;font-size:14px">
          <p style="margin:0 0 10px">
            Ordered: <b>${escapeHtml(s.ordered)}</b><br/>
            Bank credited: <b>${escapeHtml(s.credited)}</b><br/>
            ${s.kind ? `Mismatch: <b>${escapeHtml(s.kind)}</b><br/>` : ""}
            ${s.difference ? `Difference: <b>${escapeHtml(s.difference)}</b>` : ""}
          </p>
          <p style="margin:0">${escapeHtml(outcome.text)}</p>
        </div>
      `,
      confirmButtonText: "OK",
    });
  };

  /**
   * A refusal, worded from the API's code. When the refusal means the list is
   * stale (the order was paid, a receipt or reference appeared, the receipt
   * moved), the list is reloaded first so the row shows the server's state.
   */
  const showRefusal = async (error, action) => {
    const refusal = describeRefusal(error?.response?.data, action);
    if (refusal.reload) await fetchRows();
    Swal.fire({ icon: "error", title: refusal.title, text: refusal.text, confirmButtonText: "OK" });
  };

  const markPaid = async (row) => {
    // The currency options offered are NOT restricted to the campaign's. The
    // reconciler is recording what the bank did, and the case worth catching is
    // exactly the one where that is not what the campaign expects.
    const options = Array.from(
      new Set(
        ["CAD", "USD", row.payableCurrency, row.campaignCurrency, row.currency].filter(
          Boolean
        )
      )
    );
    const { value: form } = await Swal.fire({
      title: `Mark #${row.investmentId} as PAID`,
      width: 620,
      html: `
        <div style="text-align:left;font-size:14px">
          <p style="margin:0 0 10px">
            Order: <b>${escapeHtml(money(row.amount, row.currency))}</b><br/>
            This campaign is funded in <b>${escapeHtml(
              row.payableCurrency || row.campaignCurrency || "an undeclared currency"
            )}</b>.<br/>
            ${
              row.declaredAmount != null && row.declaredCurrency
                ? `The investor stated they sent <b>${escapeHtml(
                    money(row.declaredAmount, row.declaredCurrency)
                  )}</b>.`
                : "The investor did not state an amount."
            }
          </p>
          ${proofNoteHtml(row)}
          <label for="wire-bank-ref" style="display:block;margin-top:8px">
            Bank reference of the incoming wire
            (investor quoted ${escapeHtml(row.referenceCode || "no code")})
          </label>
          <input id="wire-bank-ref" class="swal2-input" style="width:100%;margin:4px 0"
                 placeholder="e.g. FT26080200123" />
          <label for="wire-received-amount" style="display:block;margin-top:8px">
            Amount the bank actually credited
          </label>
          <input id="wire-received-amount" class="swal2-input" type="number" step="0.01" min="0"
                 style="width:100%;margin:4px 0" placeholder="Plain number, no separators" />
          <label for="wire-received-currency" style="display:block;margin-top:8px">
            Currency the bank actually credited
          </label>
          <select id="wire-received-currency" class="swal2-input" style="width:100%;margin:4px 0">
            <option value="">Select a currency</option>
            ${options
              .map((c) => `<option value="${escapeHtml(c)}">${escapeHtml(c)}</option>`)
              .join("")}
          </select>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Mark paid",
      preConfirm: () => {
        const bankReference = document.getElementById("wire-bank-ref").value.trim();
        const receivedAmount = document
          .getElementById("wire-received-amount")
          .value.trim();
        const receivedCurrency = document.getElementById("wire-received-currency").value;
        if (!bankReference) {
          Swal.showValidationMessage("The bank reference is required.");
          return false;
        }
        if (!receivedAmount || !Number.isFinite(Number(receivedAmount))) {
          Swal.showValidationMessage(
            "Enter the credited amount as a plain number, without separators."
          );
          return false;
        }
        if (!receivedCurrency) {
          Swal.showValidationMessage(
            "Select the currency the bank credited. Record what arrived, not what was ordered."
          );
          return false;
        }
        return { bankReference, receivedAmount, receivedCurrency };
      },
    });
    if (!form) return;

    // R8 / D9: a credit that differs from the order is RECORDED as an
    // unresolved mismatch and the order is NOT marked paid (no tolerance, no
    // partial payments). Say so before the reconciler commits to it. A zero,
    // negative or unparseable figure gets no prediction: it is posted and the
    // server's own refusal (400 DECLARED_AMOUNT_INVALID) is shown.
    if (creditDiffers(row.amount, form.receivedAmount)) {
      const ordered = Math.round(Number(row.amount) * 100);
      const credited = Math.round(Number(form.receivedAmount) * 100);
      const delta = ((credited - ordered) / 100).toFixed(2);
      const ok = await Swal.fire({
        icon: "warning",
        title: "The credit does not match the order",
        text: differingCreditWarning(
          money(row.amount, row.currency),
          money(form.receivedAmount, form.receivedCurrency),
          delta
        ),
        showCancelButton: true,
        confirmButtonText: "Record as unresolved mismatch",
      });
      if (!ok.isConfirmed) return;
    }

    setLoading(true);
    try {
      const response = await postMarkPaid({ investmentId: row.investmentId, ...form });
      await fetchRows();
      await reportOutcome(response, row);
    } catch (error) {
      const data = error?.response?.data;
      if (data?.code === "RECEIVED_CURRENCY_MISMATCH") {
        // Never a silent pass. The reason is written to the audit log with the
        // discrepancy, so the decision can be reconstructed later.
        setLoading(false);
        const { value: reason } = await Swal.fire({
          icon: "warning",
          title: "Wrong currency credited",
          html: `The bank credited <b>${escapeHtml(data.receivedCurrency)}</b> but this
                 campaign is funded in <b>${escapeHtml(data.expectedCurrency)}</b>.<br/>
                 It can be recorded with a reason as an unresolved mismatch; the order
                 is NOT marked paid. Say why.`,
          input: "textarea",
          inputPlaceholder: "Why is this credit being recorded despite the currency?",
          showCancelButton: true,
          confirmButtonText: "Record as unresolved mismatch",
          inputValidator: (v) =>
            !v || !v.trim() ? "A reason is required to override." : undefined,
        });
        if (!reason) return;
        setLoading(true);
        try {
          const response = await postMarkPaid({
            investmentId: row.investmentId,
            ...form,
            currencyOverrideReason: reason.trim(),
          });
          await fetchRows();
          await reportOutcome(response, row);
        } catch (overrideError) {
          await showRefusal(overrideError, "record");
        } finally {
          setLoading(false);
        }
        return;
      }
      await showRefusal(error, "record");
    } finally {
      setLoading(false);
    }
  };

  /**
   * R8: withdraw the order's active receipt (AD only, audited) so a corrected
   * credit can be recorded. A MATCHED receipt's settlement is reversed with it.
   */
  const reverseReceipt = async (row) => {
    const receipt = row.activeReceipt;
    if (!receipt) return;
    const { value: reason } = await Swal.fire({
      icon: "warning",
      title: `Reverse receipt #${receipt.id}`,
      html: `${escapeHtml(receiptLabel(receipt))}: credited <b>${escapeHtml(money(receipt.amount, receipt.currency))}</b>
             against <b>${escapeHtml(money(receipt.expectedAmount, receipt.expectedCurrency))}</b>,
             reference <b>${escapeHtml(receipt.bankReference)}</b>.<br/>${
               receipt.status === "MATCHED"
                 ? "Reversing it also reverses the settlement: the order goes back to UNPAID."
                 : "No money moved for this receipt; reversing it lets the corrected credit be recorded."
             }`,
      input: "textarea",
      inputPlaceholder: "Why is this receipt being reversed? (at least 20 characters)",
      showCancelButton: true,
      confirmButtonText: "Reverse receipt",
      inputValidator: reversalReasonError,
    });
    if (!reason) return;
    // A separate confirmation, stating what moves, before anything is posted.
    const confirmation = reversalConfirmation(receipt, reason);
    const confirmed = await Swal.fire({
      icon: "warning",
      title: confirmation.title,
      html: `
        <div style="text-align:left;font-size:14px">
          <p style="margin:0 0 10px">${escapeHtml(confirmation.consequence)}</p>
          <p style="margin:0">Reason (written to the audit log): <i>${escapeHtml(confirmation.reason)}</i></p>
        </div>
      `,
      showCancelButton: true,
      focusCancel: true,
      confirmButtonText: confirmation.confirmButtonText,
      cancelButtonText: "Cancel",
    });
    if (!confirmed || !confirmed.isConfirmed) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}admin/wire/receipts/${receipt.id}/reverse`,
        { reason: confirmation.reason },
        { headers: authHeaders() }
      );
      await fetchRows();
      const outcome = reversalOutcome(res?.data);
      Swal.fire({ icon: "success", title: outcome.title, text: outcome.text, confirmButtonText: "OK" });
    } catch (error) {
      await showRefusal(error, "reverse");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save the proof under the server's filename (wire-proof-<id>.pdf for new
   * proofs, .png / .jpg for legacy ones). Content-Disposition is hidden from a
   * cross-origin page unless the API exposes it, so the Content-Type's
   * extension is the fallback (see wireReceiptUi.proofDownloadName).
   */
  const downloadProof = async (row) => {
    if (downloadingId !== null) return;
    setDownloadingId(row.investmentId);
    try {
      const res = await axios.get(`${API}admin/wire/proof/${row.investmentId}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const headers = res?.headers || {};
      const type = headers["content-type"] || (res?.data && res.data.type) || "";
      const filename = proofDownloadName(headers["content-disposition"], type, row.investmentId);
      const blob =
        res.data instanceof Blob ? res.data : new Blob([res.data], { type: type || "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Anchor-with-download (in the document, for Firefox), not window.open:
      // a popup opened after an await is blocked.
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoked on the next tick: some browsers cancel a download whose URL is
      // revoked in the same task as the click.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      // R8: the route is AD-only and audited; a refusal or a failed audit
      // write is not "no proof". The Blob error body has no code: key on status.
      const failure = proofFailure(error?.response?.status ?? null);
      Swal.fire({ icon: failure.icon, title: failure.title, text: failure.text, confirmButtonText: "OK" });
    } finally {
      setDownloadingId(null);
    }
  };

  const closeProofRequest = () => {
    if (proofSubmitting) return;
    setProofRequest(null);
  };

  /**
   * POST request-new with the reason and the token of the proof the AD
   * reviewed. The dialog is closed BEFORE any Swal opens (its focus trap would
   * otherwise hold Tab and Escape). A refusal that means the row is stale
   * (PROOF_REPLACED: the investor uploaded a newer proof) reloads the list
   * first, so the AD reviews what is on file now.
   */
  const submitProofRequest = async (reason) => {
    const row = proofRequest;
    if (!row || !row.wireProof) return;
    setProofSubmitting(true);
    try {
      const res = await axios.post(
        `${API}admin/wire/proof/${row.investmentId}/request-new`,
        { reason, proofToken: row.wireProof.proofToken },
        { headers: authHeaders() }
      );
      setProofSubmitting(false);
      setProofRequest(null);
      await fetchRows();
      const outcome = newProofRequestedOutcome({ investmentId: row.investmentId, ...(res?.data || {}) });
      Swal.fire({ icon: "success", title: outcome.title, text: outcome.text, confirmButtonText: "OK" });
    } catch (error) {
      setProofSubmitting(false);
      // Codex P2: the refusal opens only AFTER the dialog has unmounted (the effect
      // below), so its focus trap restores focus first and the Swal then takes it,
      // instead of the trap's cleanup pulling focus back behind the open modal.
      setPendingProofRefusal({ error });
      setProofRequest(null);
    }
  };

  useEffect(() => {
    if (proofRequest || !pendingProofRefusal) return;
    const { error } = pendingProofRefusal;
    setPendingProofRefusal(null);
    showRefusal(error, "requestProof");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proofRequest, pendingProofRefusal]);

  const columns = [
    { field: "investmentId", headerName: "ID", width: 80 },
    { field: "investor", headerName: "Investor", flex: 1, minWidth: 150 },
    { field: "campaign", headerName: "Campaign", flex: 1, minWidth: 160 },
    {
      field: "amount",
      headerName: "Ordered",
      width: 150,
      // The unit travels with the figure. Sorting still works on the raw value
      // because renderCell only changes what is drawn.
      renderCell: (params) => money(params.value, params.row.currency),
    },
    {
      field: "currency",
      headerName: "Order ccy",
      width: 105,
      renderCell: (params) =>
        params.row.currencyMismatch ? (
          <Chip label={params.value || "none"} color="error" size="small" />
        ) : (
          params.value || "—"
        ),
    },
    {
      field: "campaignCurrency",
      headerName: "Campaign ccy",
      width: 130,
      // A grandfathered EUR/GBP/HKD listing is payable in USD, so both are
      // shown when they differ rather than picking one and misleading on the
      // other.
      renderCell: (params) => {
        const listed = params.value || "—";
        const payable = params.row.payableCurrency;
        return payable && payable !== params.value ? `${listed} → ${payable}` : listed;
      },
    },
    { field: "referenceCode", headerName: "Reference", width: 140 },
    {
      field: "declaredSentAt",
      headerName: "Declared sent",
      width: 140,
      renderCell: (params) =>
        params.value ? (
          <Chip label={new Date(params.value).toLocaleDateString()} color="info" size="small" />
        ) : (
          "—"
        ),
    },
    {
      field: "declaredAmount",
      headerName: "Investor says",
      width: 150,
      renderCell: (params) => money(params.value, params.row.declaredCurrency),
    },
    {
      field: "receivedAmount",
      headerName: "Bank credited",
      width: 150,
      // R8: the order's own received_* are written only by a credit that
      // SETTLES, so for an open mismatch they are empty; its receipt is the
      // record of what the bank credited.
      renderCell: (params) => {
        const credit = rowCredit(params.row);
        return money(credit.amount, credit.currency);
      },
    },
    { field: "paymentStatus", headerName: "Status", width: 100 },
    {
      field: "wireProof",
      headerName: "Proof",
      width: 330,
      sortable: false,
      // Where the investor's proof of payment stands, in Toronto time. A row
      // with no proof state (not confirmed, closed) shows nothing.
      renderCell: (params) => {
        const status = proofStatus(params.value);
        if (!status) return null;
        return (
          <span
            data-testid="wire-proof-cell"
            data-state={status.state}
            data-investment-id={params.row.investmentId}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 2,
              minWidth: 0,
              whiteSpace: "normal",
              lineHeight: 1.3,
            }}
          >
            {/* The label wraps rather than ellipsizing: the date is what matters. */}
            <Chip
              label={status.label}
              color={status.color}
              size="small"
              title={status.label}
              sx={{
                maxWidth: "100%",
                height: "auto",
                "& .MuiChip-label": { whiteSpace: "normal", display: "block", py: "2px" },
              }}
            />
            {status.detail && <span style={{ fontSize: 12 }}>{status.detail}</span>}
          </span>
        );
      },
    },
    {
      field: "activeReceipt",
      headerName: "Receipt",
      width: 240,
      sortable: false,
      // R8: an open mismatch is a credit that arrived and did NOT settle; the
      // signed difference from the order is shown beside it (same currency only).
      renderCell: (params) => {
        const label = receiptLabel(params.value);
        if (!label) return "—";
        const difference = receiptDifference(params.value);
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Chip
              label={label}
              color={params.value.status === "AMOUNT_MISMATCH" ? "warning" : "success"}
              size="small"
            />
            {difference && <span style={{ fontSize: 12 }}>{difference}</span>}
          </span>
        );
      },
    },
    {
      field: "bankReference",
      headerName: "Bank ref",
      width: 140,
      renderCell: (params) => rowCredit(params.row).bankReference || "—",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 360,
      sortable: false,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {(params.row.hasProof || params.row.wireProof?.proofOnFile) && (
            <Button
              size="small"
              variant="outlined"
              data-testid="wire-proof-download"
              aria-label={`Download the proof of payment for order #${params.row.investmentId}`}
              aria-busy={downloadingId === params.row.investmentId}
              disabled={downloadingId !== null}
              onClick={() => downloadProof(params.row)}
            >
              {downloadingId === params.row.investmentId ? "Downloading…" : "Proof"}
            </Button>
          )}
          {canAskForProof && canRequestNewProof(params.row) && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              data-testid="request-new-proof-btn"
              aria-label={`Request a new proof of payment for order #${params.row.investmentId}`}
              aria-haspopup="dialog"
              disabled={proofSubmitting}
              onClick={() => setProofRequest(params.row)}
            >
              Request new proof
            </Button>
          )}
          {params.row.paymentStatus !== "PAID" && !params.row.activeReceipt && (
            <Button size="small" variant="contained" color="success" onClick={() => markPaid(params.row)}>
              Mark paid
            </Button>
          )}
          {params.row.activeReceipt && (
            <Button size="small" variant="outlined" color="warning" onClick={() => reverseReceipt(params.row)}>
              Reverse receipt
            </Button>
          )}
        </div>
      ),
    },
  ];

  const mismatched = rows.filter((r) => r.currencyMismatch).length;
  const openMismatches = rows.filter(
    (r) => r.activeReceipt && r.activeReceipt.status === "AMOUNT_MISMATCH"
  ).length;

  return (
    <PageShell>
      <PageHead title="Wire reconciliation" />
      <div className="datatable">
        <Loader isLoading={loading} />
        <div className="datatableTitle">
          <h5>Wire transfers</h5>
          <div>
            <label htmlFor="wire-status" style={{ fontSize: 14, marginRight: 8 }}>
              Status
            </label>
            <select
              id="wire-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: "6px 12px", borderRadius: 4, border: "1px solid #ccc" }}
            >
              <option value="UNPAID">UNPAID</option>
              <option value="PAID">PAID</option>
              <option value="MISMATCH">Open mismatches</option>
              <option value="PROOF_RECEIVED">Proof received, not verified</option>
              <option value="">All</option>
            </select>
          </div>
        </div>
        {mismatched > 0 && (
          <p role="alert" style={{ color: "#b71c1c", fontWeight: 600, margin: "0 0 8px" }}>
            {mismatched} order{mismatched === 1 ? " is" : "s are"} denominated in a
            currency this campaign is not funded in. These predate the denomination
            gate and cannot be settled. Escalate rather than overriding.
          </p>
        )}
        {openMismatches > 0 && (
          <p
            data-testid="open-mismatch-banner"
            role="status"
            style={{ color: "#8a4b00", fontWeight: 600, margin: "0 0 8px" }}
          >
            {openMismatches} open mismatch{openMismatches === 1 ? "" : "es"} in view:
            a credit was recorded and did NOT settle its order (no partial payments).
            Reverse the receipt with a reason, then record the corrected credit.
          </p>
        )}
        <DataGrid
          className="datagrid"
          autoHeight
          rows={rows}
          getRowId={(r) => r.investmentId}
          getRowClassName={(params) =>
            params.row.currencyMismatch ? "wire-currency-mismatch" : ""
          }
          // A proof with a second line ("May expire from ...") or a row offering
          // Request new proof needs more than the default height (5.10 has no
          // 'auto' row height); every other row keeps the default.
          getRowHeight={({ model }) =>
            proofStatus(model && model.wireProof)?.detail ||
            (canAskForProof && canRequestNewProof(model))
              ? 76
              : null
          }
          sx={{
            "& .wire-currency-mismatch": {
              backgroundColor: "rgba(211,47,47,0.08)",
            },
          }}
          columns={columns}
          hideFooterPagination
        />
      </div>
      {proofRequest && (
        <RequestNewProofDialog
          row={proofRequest}
          submitting={proofSubmitting}
          onCancel={closeProofRequest}
          onSubmit={submitProofRequest}
        />
      )}
    </PageShell>
  );
};

export default WireReconciliation;
